import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { cellKey, resolveCell, type ResolvedCell } from '@/lib/tableForm'
import { cellTestCode } from '@/lib/lis/fieldStage'
import { shownTestCode, testCodeText, type ShownTestCode } from '@/lib/lis/testCodeLabel'
import type { TableColumn, TableConfig, TableRow } from '@/types/cgt'

/**
 * The fill-time grid shared by an inline table field (FormRenderer) and a
 * table-layout form (TableFormRenderer): header row, row-header column and
 * cells marked not applicable. What goes inside every other cell stays with
 * each renderer, through `renderCell`, which returns the whole `<td>`.
 *
 * `showColumnHeader` / `showRowHeader` (z8nrz7eujb) hide the header row and the
 * row-label column. Absent means shown, so a table saved before the switches
 * existed draws the same markup as before.
 *
 * A cell connected to a LIS test code (z8nrz7g2wm) names it on hover, so the
 * person filling the grid can see which test each value belongs to. Added here
 * rather than in each `renderCell`, so both renderers get it; a cell that sets
 * its own `title` keeps it.
 */
export function TableGrid({
  config,
  rows,
  className,
  renderCell,
  receivedTestCodes,
}: {
  config: TableConfig
  /** Predefined rows plus the rows added at fill time. */
  rows: TableRow[]
  className?: string
  /** Cell key → the test code the LIS sent the cell's current value with. */
  receivedTestCodes?: ReadonlyMap<string, string>
  renderCell: (row: TableRow, col: TableColumn, key: string, resolved: ResolvedCell) => ReactNode
}) {
  const showRowHeader = config.showRowHeader !== false
  const showColumnHeader = config.showColumnHeader !== false

  return (
    <div className={cn('overflow-x-persistent rounded-lg border', className)}>
      <table className='w-full border-collapse text-sm'>
        {showColumnHeader && (
          <thead>
            <tr>
              {showRowHeader && (
                <th className='sticky left-0 z-10 border bg-table-header px-3 py-2 text-left font-semibold text-table-header-foreground'>
                  {config.rowHeaderLabel || ' '}
                </th>
              )}
              {config.columns.map(col => (
                <th
                  key={col.id}
                  title={col.lisTestCode ? `Test code: ${col.lisTestCode}` : undefined}
                  className='border bg-table-header px-3 py-2 text-left font-semibold text-table-header-foreground'
                >
                  {col.label || 'Untitled'}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map(row => (
            <tr key={row.id}>
              {showRowHeader && (
                <th className='sticky left-0 z-10 border bg-muted/20 px-3 py-2 text-left font-medium'>
                  {row.label}
                </th>
              )}
              {config.columns.map(col => {
                const key = cellKey(row.id, col.id)
                const resolved = resolveCell(config, row.id, col)
                // Not applicable: shaded like a calculated cell, with no
                // control and no value.
                if (resolved.disabled) {
                  return (
                    <td
                      key={col.id}
                      aria-disabled='true'
                      title='Not applicable'
                      className='border bg-muted px-3 py-2'
                    />
                  )
                }
                return withTestCode(
                  renderCell(row, col, key, resolved),
                  shownTestCode(cellTestCode(config, row.id, col), receivedTestCodes?.get(key)),
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Puts the cell's test code on its `<td>` as a hover title, unless it has a title already. */
function withTestCode(cell: ReactNode, shown: ShownTestCode | null): ReactNode {
  if (!shown || !isValidElement(cell)) return cell
  const el = cell as ReactElement<{ title?: string }>
  if (el.props.title) return el
  return cloneElement(el, { title: testCodeText(shown), 'data-lis-test-code': shown.code } as Record<string, string>)
}

/**
 * "Add row" under a grid that lets respondents add rows. With the row-header
 * column hidden a row label is never shown, so none is asked for: the row is
 * numbered instead, which still names it in an image caption.
 */
export function TableAddRow({
  config,
  rowCount,
  onAdd,
}: {
  config: TableConfig
  /** Rows in the grid now, predefined and added, for numbering a new one. */
  rowCount: number
  onAdd: (label: string) => Promise<void>
}) {
  const [label, setLabel] = useState('')
  const [adding, setAdding] = useState(false)
  const askLabel = config.showRowHeader !== false

  const add = async () => {
    const next = askLabel ? label.trim() : `Row ${rowCount + 1}`
    if (!next) return
    setAdding(true)
    await onAdd(next)
    setLabel('')
    setAdding(false)
  }

  return (
    <div className='flex items-center gap-2'>
      {askLabel && (
        <Input
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder={`New ${config.rowHeaderLabel.toLowerCase() || 'row'}…`}
          className='h-9 max-w-xs'
          onKeyDown={e => e.key === 'Enter' && add()}
        />
      )}
      <Button variant='outline' size='sm' onClick={add} disabled={(askLabel && !label.trim()) || adding}>
        {adding ? (
          <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
        ) : (
          <Plus className='mr-1.5 h-3.5 w-3.5' />
        )}
        Add row
      </Button>
    </div>
  )
}
